import { Alert, Box, Button, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList, type MetadataItem } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { CommissionCalculationPanel } from './CommissionCalculationPanel';
import { ReversalChainPanel } from './ReversalChainPanel';
import type { TransactionGapRepository } from './mockRepository';
import { TransactionReversalDialog, isReversalEligible } from './TransactionReversalDialog';
import { toTransactionPageError } from './TransactionListPage';
import { transactionRepositoryToken } from './repository';
import type { TransactionDetail } from './types';
import { formatTransactionMoney, toSafeFailure } from './types';

export function TransactionDetailPage({
  dataSource,
  reversalDataSource,
  reversalRepository,
}: {
  readonly dataSource: DataSource;
  readonly reversalDataSource: DataSource;
  readonly reversalRepository: TransactionGapRepository | null;
}) {
  const { transactionId = '' } = useParams();
  const repository = useRepository(transactionRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [reversalOpen, setReversalOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.get(transactionId, signal),
    [repository, transactionId, refreshKey],
    () => false,
  );
  const permissions =
    auth.status === 'authenticated' ? new Set(auth.principal.permissions) : new Set<string>();
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toTransactionPageError(resource.error, t('transactions.errors.detail'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              actions={
                <Button
                  disabled={reversalDataSource === 'mock' && !isReversalEligible(resource.data)}
                  onClick={() => setReversalOpen(true)}
                  variant="outlined"
                >
                  {t('transactions.reversal.action')}
                </Button>
              }
              backTo={paths.transactions}
              dataSource={dataSource}
              subtitle={t('transactions.detail.immutable')}
              title={resource.data.partnerTransactionId}
            />
            {resource.data.requestId === null ? null : (
              <Alert severity="info">
                {t('transactions.detail.requestId')}: <span>{resource.data.requestId}</span>
              </Alert>
            )}
            <DetailSection
              items={overviewItems(resource.data, permissions, language, t)}
              title={t('transactions.detail.overview')}
            />
            <DetailSection
              items={relationshipItems(resource.data, permissions, t)}
              title={t('transactions.detail.relationshipSnapshot')}
            />
            <CommissionCalculationPanel
              detail={resource.data}
              language={language}
              permissions={permissions}
            />
            <ReversalChainPanel
              detail={resource.data}
              language={language}
              permissions={permissions}
            />
            <TracePanel detail={resource.data} language={language} />
            {reversalDataSource === 'mock' && !isReversalEligible(resource.data) ? (
              <Alert severity="warning">{t('transactions.reversal.ineligible')}</Alert>
            ) : null}
            <TransactionReversalDialog
              dataSource={reversalDataSource}
              detail={resource.data}
              onClose={() => setReversalOpen(false)}
              open={reversalOpen}
              repository={reversalRepository}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function DetailSection({
  items,
  title,
}: {
  readonly items: readonly MetadataItem[];
  readonly title: string;
}) {
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {title}
      </Typography>
      <MetadataList items={items} />
    </Paper>
  );
}

function overviewItems(
  detail: TransactionDetail,
  permissions: ReadonlySet<string>,
  language: TimeLanguage,
  t: ReturnType<typeof useTranslation>['t'],
): readonly MetadataItem[] {
  const play = displayTime(detail.playAt, language);
  const received = displayTime(detail.receivedAt, language);
  const partner = linked(
    permissions,
    'PARTNER_VIEW',
    `${paths.partners}/${encodeURIComponent(detail.partner.id)}`,
    t('transactions.detail.partnerLink', { name: detail.partner.name }),
  );
  return [
    { label: t('transactions.fields.partner'), value: partner },
    { label: t('transactions.fields.transactionId'), value: detail.id },
    { label: t('transactions.fields.partnerTransactionId'), value: detail.partnerTransactionId },
    { label: t('transactions.fields.status'), value: detail.persistedStatus },
    { label: t('transactions.fields.reversalState'), value: detail.reversalState },
    { label: t('transactions.fields.username'), value: detail.submittedUsername },
    {
      label: t('transactions.fields.partnerUserId'),
      value: detail.submittedPartnerUserId ?? t('common.notAvailable'),
    },
    {
      label: t('transactions.fields.gameTypeCode'),
      value: detail.submittedGameTypeCode ?? t('common.notAvailable'),
    },
    { label: t('transactions.fields.gameCode'), value: detail.submittedGameCode },
    {
      label: t('transactions.fields.gameRound'),
      value: detail.gameRound ?? t('common.notAvailable'),
    },
    { label: t('transactions.fields.actualPlayAmount'), value: formatTransactionMoney(detail) },
    { label: t('transactions.fields.outcome'), value: detail.outcome },
    {
      label: t('transactions.fields.winLossAmount'),
      value: `${detail.currencyCode}\u00a0${detail.winLossAmount}`,
    },
    { label: t('transactions.fields.playAt'), value: play },
    { label: t('transactions.fields.receivedAt'), value: received },
    {
      label: t('transactions.fields.audit'),
      value: linked(
        permissions,
        'AUDIT_VIEW',
        `${paths.audit}?entityType=TRANSACTION&entityId=${encodeURIComponent(detail.id)}`,
        t('transactions.detail.auditLink'),
      ),
    },
  ];
}

function relationshipItems(
  detail: TransactionDetail,
  permissions: ReadonlySet<string>,
  t: ReturnType<typeof useTranslation>['t'],
): readonly MetadataItem[] {
  const r = detail.resolution;
  if (r === null)
    return [
      {
        label: t('transactions.detail.relationshipSnapshot'),
        value: t('transactions.detail.unresolved'),
      },
    ];
  return [
    {
      label: t('transactions.fields.agentId'),
      value: linked(
        permissions,
        'AGENT_VIEW',
        `${paths.agents}/${encodeURIComponent(r.agentId)}`,
        t('transactions.detail.agentLink', { code: r.agentCode }),
      ),
    },
    {
      label: t('transactions.fields.memberId'),
      value: linked(
        permissions,
        'MEMBER_VIEW',
        `${paths.members}/${encodeURIComponent(r.memberId)}`,
        t('transactions.detail.memberLink', { username: r.downlineUsername }),
      ),
    },
    {
      label: t('transactions.fields.uplineMemberId'),
      value: linked(
        permissions,
        'MEMBER_VIEW',
        `${paths.members}/${encodeURIComponent(r.uplineMemberId)}`,
        r.uplineUsername,
      ),
    },
    {
      label: t('transactions.fields.gameId'),
      value: linked(
        permissions,
        'GAME_VIEW',
        `${paths.games}/${encodeURIComponent(r.gameId)}`,
        t('transactions.detail.gameLink', { code: r.gameCode }),
      ),
    },
  ];
}

function TracePanel({
  detail,
  language,
}: {
  readonly detail: TransactionDetail;
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  const idempotency = detail.idempotency;
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h5">
        {t('transactions.detail.requestTrace')}
      </Typography>
      <Alert severity="info" sx={{ my: 2 }}>
        {t('transactions.detail.safeMetadata', { count: detail.requestTrace.fieldCount })}
      </Alert>
      <MetadataList
        items={[
          {
            label: t('transactions.fields.trace'),
            value: detail.requestTrace.trace ?? t('common.notAvailable'),
          },
          {
            label: t('transactions.fields.failureCode'),
            value: detail.failure?.code ?? t('common.notAvailable'),
          },
          {
            label: t('transactions.fields.failureSummary'),
            value: safeFailureSummary(detail.failure) || t('common.notAvailable'),
          },
          { label: t('transactions.fields.idempotencyId'), value: idempotency.id },
          { label: t('transactions.fields.idempotencyKey'), value: idempotency.keyHint },
          { label: t('transactions.fields.idempotencyState'), value: idempotency.state },
          {
            label: t('transactions.fields.responseStatus'),
            value: idempotency.responseStatus ?? t('common.notAvailable'),
          },
          {
            label: t('transactions.fields.createdAt'),
            value: displayTime(idempotency.createdAt, language),
          },
          {
            label: t('transactions.fields.completedAt'),
            value:
              idempotency.completedAt === null
                ? t('common.notAvailable')
                : displayTime(idempotency.completedAt, language),
          },
          {
            label: t('transactions.fields.expiresAt'),
            value: displayTime(idempotency.expiresAt, language),
          },
        ]}
      />
    </Paper>
  );
}

function linked(
  permissions: ReadonlySet<string>,
  permission: string,
  to: string,
  label: string,
): ReactNode {
  return permissions.has(permission) ? (
    <MuiLink component={Link} to={to}>
      {label}
    </MuiLink>
  ) : (
    label
  );
}
function displayTime(value: string, language: TimeLanguage) {
  const time = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${time.display} (${time.originalOffset})`;
}
function safeFailureSummary(failure: TransactionDetail['failure']): string {
  if (failure === null) return '';
  if (Array.isArray(failure.summary)) return failure.summary.join(', ');
  const candidate = failure as unknown as {
    readonly code: string;
    readonly details?: Readonly<Record<string, unknown>> | null;
  };
  return (
    toSafeFailure({ code: candidate.code, details: candidate.details ?? null })?.summary.join(
      ', ',
    ) ?? ''
  );
}
